import React, { useState } from "react";
import { Container, Col, Row, Table, Input, Label, FormGroup, Alert } from "reactstrap";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button as ModalButton } from "reactstrap";
import { Crumb } from "@repoflow/components/breadcrumb";
import * as Notify from "@repoflow/form-schema/notify";
import * as Section from "@repoflow/components/section";
import { Mutation, Button } from "@repoflow/components/section";
import * as KeysQL from "queries/key";

// "Connect an agent": mint a key bound to THIS site for somebody else's agent,
// show how to connect it, and list and revoke the site's agent keys.
// docs/pagews.md §Connect an agent.
//
// Every key minted here is `roles: ["agent"]` with this site's id — the one
// shape auth-repoflow accepts for a bound key — so it reads this site and
// writes its drafts and nothing else, and it authenticates nowhere but pagews.
// The organization-wide Keys screen is still where every other key is made; it
// offers no site field, so this is the only place a bound key comes from.

// The API is served from the app's own host (CloudFront's `/api/*`), so the MCP
// endpoint is this origin's.
export const mcpUrlOf = (origin) => `${origin}/api/mcp`;

export const connectionOf = (origin, keyId, keySecret) => {
  const url = mcpUrlOf(origin);
  const header = `Authorization: Bearer ${keyId}/${keySecret}`;
  return {
    url,
    header,
    claudeCode: `claude mcp add --transport http pagews ${url} --header "${header}"`,
  };
};

const origin = () => window.location.origin;

export const Sidebar = () => null;

export const Indice = () => {
  const { base } = Section.useSectionContext();
  return <Crumb to={`${base}/agent`} label={"Agents"} />;
};

export const Content = ({ match: { params } }) => {
  const [minted, setMinted] = useState(null);

  return (
    <Container>
      <div className="mt-4" />

      <Row>
        <Col>
          <p>
            An agent key lets somebody else's AI agent work on <b>this site only</b>: it can
            read the site and write <b>drafts</b> — pages, blog posts and files. It cannot
            publish, remove anything that is live, or change sections, packs, domains or the
            site's settings; everything it writes waits for you to publish it here.
          </p>
          <p className="mb-1">The agent connects to this site's MCP server:</p>
          <p>
            <code>{mcpUrlOf(origin())}</code> with the header{" "}
            <code>Authorization: Bearer &lt;keyId&gt;/&lt;keySecret&gt;</code>
          </p>
        </Col>
      </Row>

      <Mint siteId={params.siteId} onMinted={setMinted} />

      <SecretModal minted={minted} onClose={() => setMinted(null)} />

      <div className="mt-4" />

      <Section.Query query={KeysQL.agents} variables={{ siteId: params.siteId }} datapath={"keys.agents"}>
        {({ values }) => <Listing listing={values} />}
      </Section.Query>
    </Container>
  );
};

// The input sits OUTSIDE the Mutation, and the Mutation's child is a component
// defined at module scope: @repoflow/components/section renders that child as
// an element, so an inline function there is a new component type on every
// render — each keystroke would remount the input and lose what was typed.
const Mint = ({ siteId, onMinted }) => {
  const [description, setDescription] = useState("");

  return (
    <Row className="align-items-end">
      <Col md={6}>
        <FormGroup className="mb-0">
          <Label for="agent-key-description">Who is it for?</Label>
          <Input
            id="agent-key-description"
            placeholder="e.g. Ana's writing assistant"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </FormGroup>
      </Col>
      <Col md="auto">
        <Mutation
          mutation={KeysQL.create}
          update={KeysQL.update({})}
          onMutate={({ handler }) => {
            const trimmed = description.trim();
            handler({
              variables: {
                input: {
                  roles: ["agent"],
                  scope: { service: "pagews", type: "site", id: siteId },
                  ...(trimmed ? { description: trimmed } : {}),
                },
              },
            });
          }}
          onCompleted={(data) => {
            const key = data.viewer.account.keys.create;
            setDescription("");
            onMinted({ keyId: key.keyId, keySecret: key.keySecret });
          }}
          onError={(error) => {
            Notify.error(error.toString());
          }}
        >
          {MintButton}
        </Mutation>
      </Col>
    </Row>
  );
};

const MintButton = ({ mutate, loading }) => (
  <Button color="primary" loading={loading} onClick={() => mutate()}>
    <i className="fa fa-plus" /> Mint an agent key
  </Button>
);

// The secret, exactly once: auth-repoflow stores its bcrypt hash and hands the
// plaintext back only in the create response, so closing this is the last
// anybody sees of it.
const SecretModal = ({ minted, onClose }) => {
  const connection = minted ? connectionOf(origin(), minted.keyId, minted.keySecret) : null;

  return (
    <Modal isOpen={minted !== null} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>Agent key {minted && minted.keyId}</ModalHeader>
      {connection && (
        <ModalBody>
          <Alert color="warning">
            Copy this now. The secret is shown once and cannot be recovered — if it is lost,
            revoke the key and mint another.
          </Alert>
          <p className="mb-1">MCP server</p>
          <pre className="bg-light p-2">{connection.url}</pre>
          <p className="mb-1">Header</p>
          <pre className="bg-light p-2" style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
            {connection.header}
          </pre>
          <p className="mb-1">Claude Code</p>
          <pre className="bg-light p-2" style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
            {connection.claudeCode}
          </pre>
          <p className="text-muted small mb-0">
            Any MCP client that can send a header works. Claude.ai and ChatGPT connectors cannot
            use a key yet.
          </p>
        </ModalBody>
      )}
      <ModalFooter>
        <ModalButton color="primary" onClick={onClose}>
          I have copied it
        </ModalButton>
      </ModalFooter>
    </Modal>
  );
};

const Listing = ({ listing }) => {
  const items = (listing && listing.items) || [];

  return (
    <>
      {listing && listing.complete === false && (
        <Alert color="warning">
          This organization has more keys than one look can read; some of this site's agent keys
          may be missing here. The Keys screen lists them all.
        </Alert>
      )}
      {items.length === 0 ? (
        <p className="text-muted">No agent keys for this site.</p>
      ) : (
        <Table responsive>
          <thead>
            <tr>
              <th>Key</th>
              <th>For</th>
              <th>Created</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((key) => (
              <tr key={key.keyId}>
                <td>
                  <code>{key.keyId}</code>
                </td>
                <td>{key.description || <span className="text-muted">—</span>}</td>
                <td>{key.createdAt}</td>
                <td className="text-nowrap">
                  <RevokeButton keyId={key.keyId} />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
};

// Two presses, because a revoke cannot be undone and the agent holding the key
// stops working the moment it lands. Inline rather than a browser confirm().
// The child is a module-level component for the reason given on Mint.
const RevokeButton = ({ keyId }) => {
  const [armed, setArmed] = useState(false);

  return (
    <Mutation
      mutation={KeysQL.remove}
      update={KeysQL.update({ keyId })}
      context={{ keyId, armed, setArmed }}
      onMutate={({ handler }) => {
        handler({ variables: { keyId } });
      }}
      onCompleted={() => {
        Notify.success(`${keyId} revoked.`);
      }}
      onError={(error) => {
        setArmed(false);
        Notify.error(error.toString());
      }}
    >
      {RevokeControls}
    </Mutation>
  );
};

const RevokeControls = ({ mutate, context: { keyId, armed, setArmed } }) =>
  armed ? (
    <>
      <Button color="danger" onClick={() => mutate()}>
        Revoke {keyId}
      </Button>{" "}
      <Button color="secondary" onClick={() => setArmed(false)}>
        Keep
      </Button>
    </>
  ) : (
    <Button color="outline-danger" onClick={() => setArmed(true)}>
      Revoke
    </Button>
  );
